import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AsyncStatus } from '@/shared/types/asyncState';
import { normalizeCpfCnpj } from '@/shared/utils/documentValidation';

import type {
  CampoEmpresa,
  CanalAquisicao,
  CnpjStatus,
  CriarContaResultado,
  EmpresaForm,
  ErroCriacaoConta,
  OnboardingSelfStep,
  ProdutoId,
  RascunhoOnboardingSelf,
  VolumeCasosMes,
} from '../types';
import { ERRO_CRIACAO_DESCONHECIDO } from '../utils/erroCriacaoConta';
import {
  carregarTermosOnboardingSelf,
  consultarCnpjOnboardingSelf,
  criarContaOnboardingSelf,
} from './actions';

interface OnboardingSelfState {
  step: OnboardingSelfStep;
  empresa: EmpresaForm;
  razaoSocialReceita: string;
  cnpjStatus: CnpjStatus;
  termosVersao: string | null;
  termosVersaoAceita: string | null;
  termosStatus: AsyncStatus;
  produtos: ProdutoId[];
  volume: VolumeCasosMes | null;
  canal: CanalAquisicao | null;
  criandoConta: boolean;
  /** Modo da última criação — "Tentar novamente" repete exatamente o mesmo payload. */
  incluirRespostasDaOperacao: boolean;
  criarContaErro: ErroCriacaoConta | null;
  conta: CriarContaResultado | null;
  avisoNotificadoAberto: boolean;
  rascunhoHidratado: boolean;
}

const empresaVazia: EmpresaForm = {
  cnpj: '',
  responsavelNome: '',
  responsavelEmail: '',
  termosAceitos: false,
};

const initialState: OnboardingSelfState = {
  step: 'inicio',
  empresa: empresaVazia,
  razaoSocialReceita: '',
  cnpjStatus: 'idle',
  termosVersao: null,
  termosVersaoAceita: null,
  termosStatus: 'idle',
  produtos: [],
  volume: null,
  canal: null,
  criandoConta: false,
  incluirRespostasDaOperacao: true,
  criarContaErro: null,
  conta: null,
  avisoNotificadoAberto: false,
  rascunhoHidratado: false,
};

const respostaDeOutroCnpj = (state: OnboardingSelfState, cnpjConsultado: string) =>
  normalizeCpfCnpj(cnpjConsultado) !== normalizeCpfCnpj(state.empresa.cnpj);

const onboardingSelfSlice = createSlice({
  name: 'onboardingSelf',
  initialState,
  reducers: {
    setStep: (state, action: PayloadAction<OnboardingSelfStep>) => {
      state.step = action.payload;
    },
    setCampoEmpresa: (state, action: PayloadAction<{ campo: CampoEmpresa; valor: string }>) => {
      state.empresa[action.payload.campo] = action.payload.valor;
    },
    setTermosAceitos: (state, action: PayloadAction<boolean>) => {
      state.empresa.termosAceitos = action.payload;
      state.termosVersaoAceita = action.payload ? state.termosVersao : null;
    },
    resetCnpjStatus: (state) => {
      state.cnpjStatus = 'idle';
      state.razaoSocialReceita = '';
    },
    alternarProduto: (state, action: PayloadAction<ProdutoId>) => {
      state.produtos = state.produtos.includes(action.payload)
        ? state.produtos.filter((produto) => produto !== action.payload)
        : [...state.produtos, action.payload];
    },
    setVolume: (state, action: PayloadAction<VolumeCasosMes>) => {
      state.volume = action.payload;
    },
    setCanal: (state, action: PayloadAction<CanalAquisicao>) => {
      // Segundo clique no mesmo canal limpa a resposta: a pergunta é opcional e
      // sem isso não haveria como desmarcar depois de responder por engano.
      state.canal = state.canal === action.payload ? null : action.payload;
    },
    abrirAvisoNotificado: (state) => {
      state.avisoNotificadoAberto = true;
    },
    fecharAvisoNotificado: (state) => {
      state.avisoNotificadoAberto = false;
    },
    limparErroCriacao: (state) => {
      state.criarContaErro = null;
    },
    hidratarRascunho: (state, action: PayloadAction<RascunhoOnboardingSelf | null>) => {
      state.rascunhoHidratado = true;
      if (!action.payload) return;
      state.empresa = action.payload.empresa;
      state.produtos = action.payload.produtos;
      state.volume = action.payload.volume;
      state.canal = action.payload.canal;
      state.termosVersaoAceita = action.payload.termosVersaoAceita;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(carregarTermosOnboardingSelf.pending, (state) => {
        state.termosStatus = 'loading';
      })
      .addCase(carregarTermosOnboardingSelf.fulfilled, (state, action) => {
        state.termosStatus = 'success';
        state.termosVersao = action.payload.versao;
        // Marcado antes de a versão chegar: o aceite é desta, a que a tela passou a exibir.
        if (state.empresa.termosAceitos && state.termosVersaoAceita === null) {
          state.termosVersaoAceita = action.payload.versao;
        }
      })
      .addCase(carregarTermosOnboardingSelf.rejected, (state) => {
        state.termosStatus = 'error';
      })
      .addCase(consultarCnpjOnboardingSelf.pending, (state) => {
        state.cnpjStatus = 'consultando';
        state.razaoSocialReceita = '';
      })
      .addCase(consultarCnpjOnboardingSelf.fulfilled, (state, action) => {
        if (respostaDeOutroCnpj(state, action.meta.arg)) return;
        state.cnpjStatus = action.payload.status;
        state.razaoSocialReceita = action.payload.razaoSocial ?? '';
      })
      .addCase(consultarCnpjOnboardingSelf.rejected, (state, action) => {
        // Consulta abortada = o CNPJ mudou no meio do caminho; quem assumiu o
        // lugar dela já colocou o estado em `consultando`.
        if (action.meta.aborted || respostaDeOutroCnpj(state, action.meta.arg)) return;
        state.cnpjStatus = action.payload ?? 'indisponivel';
      })
      .addCase(criarContaOnboardingSelf.pending, (state, action) => {
        state.step = 'criando';
        state.criandoConta = true;
        state.criarContaErro = null;
        state.incluirRespostasDaOperacao = action.meta.arg.incluirRespostasDaOperacao;
      })
      .addCase(criarContaOnboardingSelf.fulfilled, (state, action) => {
        state.criandoConta = false;
        state.conta = action.payload;
        state.step = 'presente';
      })
      .addCase(criarContaOnboardingSelf.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.criandoConta = false;
        state.criarContaErro = action.payload ?? ERRO_CRIACAO_DESCONHECIDO;
      });
  },
});

export const {
  abrirAvisoNotificado,
  alternarProduto,
  fecharAvisoNotificado,
  hidratarRascunho,
  limparErroCriacao,
  resetCnpjStatus,
  setCampoEmpresa,
  setCanal,
  setStep,
  setTermosAceitos,
  setVolume,
} = onboardingSelfSlice.actions;

export default onboardingSelfSlice.reducer;
